import React, { useEffect, useState } from 'react';

/**
 * columns: [{ key, label, align, format, width, className }]
 * rows:    array of objects keyed by column.key
 * footer:  optional totals row
 * transpose: when true, swaps axes so each column becomes a row.
 */

const WRAP   = 'overflow-x-auto border border-border rounded-sm';
const BASE   = 'w-full border-collapse text-[0.85rem] font-mono bg-surface';
const HEAD   = 'px-3 py-[0.55rem] font-semibold text-text-muted text-[0.72rem] uppercase tracking-[0.06em] bg-surface-el border-b border-border whitespace-nowrap';
const CELL   = 'px-3 py-2 border-b border-border text-text';
const FOOTCL = 'px-3 py-2 bg-surface-el border-t-2 border-border-strong font-semibold text-text';
const STICKY = 'sticky left-0 bg-surface text-left font-semibold border-r border-border z-[1]';
const PAGER  = 'mt-2 flex flex-wrap items-center justify-between gap-2 border border-border rounded-sm bg-surface-el px-3 py-2 text-[0.72rem] text-text-muted';
const PAGER_GROUP = 'inline-flex items-center gap-2';
const PAGER_BUTTON = 'border border-border rounded-sm bg-surface px-2 py-[0.3rem] text-[0.72rem] font-semibold text-text-muted transition-colors enabled:cursor-pointer enabled:hover:border-border-strong enabled:hover:text-text disabled:cursor-not-allowed disabled:opacity-45';

function align(a) {
  return a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';
}

function rowClasses(r, { striped, hoverable, idx }) {
  const parts = [];
  if (striped && idx % 2 === 1) parts.push('bg-white/[0.015]');
  if (hoverable) parts.push('hover:bg-surface-hover');
  if (r.__rowClass === 'row-total') {
    parts.push('bg-surface-el font-semibold [&>td]:border-t-2 [&>td]:border-border-strong');
  } else if (r.__rowClass) {
    parts.push(r.__rowClass);
  }
  return parts.join(' ');
}

function normalizePageSize(pageSize) {
  const parsed = Number(pageSize);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 25;
}

function clampPage(page, pageCount) {
  const parsed = Number(page);
  const safePage = Number.isFinite(parsed) ? Math.floor(parsed) : 1;
  return Math.min(Math.max(1, safePage), Math.max(1, pageCount));
}

function PaginationControls({
  label,
  page,
  pageCount,
  pageSize,
  totalRows,
  onPrevious,
  onNext,
}) {
  const firstRow = totalRows === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const lastRow = Math.min(page * pageSize, totalRows);
  const safeLabel = String(label || 'Table rows');

  return (
    <nav className={PAGER} aria-label={`${safeLabel} pagination`}>
      <span aria-live="polite">
        {safeLabel}: {firstRow}-{lastRow} of {totalRows}
      </span>
      <span className={PAGER_GROUP}>
        <button
          type="button"
          className={PAGER_BUTTON}
          onClick={onPrevious}
          disabled={page <= 1}
          aria-label={`Previous ${safeLabel.toLowerCase()} page`}
        >
          Previous
        </button>
        <span className="tabular-nums text-text-muted">Page {page} / {pageCount}</span>
        <button
          type="button"
          className={PAGER_BUTTON}
          onClick={onNext}
          disabled={page >= pageCount}
          aria-label={`Next ${safeLabel.toLowerCase()} page`}
        >
          Next
        </button>
      </span>
    </nav>
  );
}

export default function DataTable({
  columns, rows, footer, compact,
  striped = true, hoverable = true, transpose = false,
  pagination = false, pageSize = 25, paginationLabel = 'Table rows',
}) {
  const compactHead = compact ? 'px-[0.6rem] py-[0.45rem]' : '';
  const compactCell = compact ? 'px-[0.6rem] py-[0.4rem] text-[0.80rem]' : '';
  const allRows = Array.isArray(rows) ? rows : [];
  const normalizedPageSize = normalizePageSize(pageSize);
  const paginationEnabled = !!pagination && !transpose && allRows.length > normalizedPageSize;
  const pageCount = paginationEnabled ? Math.ceil(allRows.length / normalizedPageSize) : 1;
  const [page, setPage] = useState(1);
  const activePage = clampPage(page, pageCount);
  const pageStartIndex = paginationEnabled ? (activePage - 1) * normalizedPageSize : 0;
  const visibleRows = paginationEnabled
    ? allRows.slice(pageStartIndex, pageStartIndex + normalizedPageSize)
    : allRows;

  useEffect(() => {
    setPage(1);
  }, [allRows.length, normalizedPageSize, pagination]);

  useEffect(() => {
    setPage((current) => clampPage(current, pageCount));
  }, [pageCount]);

  if (transpose) {
    const labelCol = columns[0];
    const metricCols = columns.slice(1);

    return (
      <div className={WRAP}>
        <table className={BASE}>
          <thead>
            <tr>
              <th className={`${HEAD} ${compactHead} text-left ${STICKY}`}>{labelCol.label}</th>
              {allRows.map((r, i) => {
                const raw = r[labelCol.key];
                const val = labelCol.format ? labelCol.format(raw, r) : raw;
                return <th key={r.id ?? r.key ?? i} className={`${HEAD} ${compactHead} text-right whitespace-nowrap`}>{val}</th>;
              })}
              {footer && <th className={`${HEAD} ${compactHead} text-right text-text font-semibold`}>{footer[labelCol.key] ?? 'Total'}</th>}
            </tr>
          </thead>
          <tbody>
            {metricCols.map((c) => (
              <tr key={c.key} className={hoverable ? 'hover:bg-surface-hover' : ''}>
                <th className={`${CELL} ${compactCell} text-left ${STICKY}`}>{c.label}</th>
                {allRows.map((r, i) => {
                  const raw = r[c.key];
                  const val = c.format ? c.format(raw, r) : raw;
                  return (
                    <td key={r.id ?? r.key ?? i} className={`${CELL} ${compactCell} ${align(c.align || 'right')} ${c.className || ''}`.trim()}>
                      {val}
                    </td>
                  );
                })}
                {footer && (() => {
                  const raw = footer[c.key];
                  const val = c.format ? c.format(raw, footer) : raw;
                  return <td className={`${FOOTCL} ${compactCell} ${align(c.align || 'right')} ${c.className || ''}`.trim()}>{val}</td>;
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const table = (
    <div className={WRAP}>
      <table className={BASE}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${HEAD} ${compactHead} ${align(c.align || 'left')}`}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => {
            const rowIndex = pageStartIndex + i;
            return (
              <tr key={r.id ?? r.key ?? rowIndex} className={rowClasses(r, { striped, hoverable, idx: rowIndex })}>
                {columns.map((c) => {
                  const raw = r[c.key];
                  const val = c.format ? c.format(raw, r) : raw;
                  return (
                    <td key={c.key} className={`${CELL} ${compactCell} ${align(c.align || 'left')} ${c.className || ''}`.trim()}>
                      {val}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {/* remove last-row bottom border to match original .data-table tbody tr:last-child td { border-bottom: none; } */}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              {columns.map((c) => {
                const raw = footer[c.key];
                const val = c.format ? c.format(raw, footer) : raw;
                return (
                  <td key={c.key} className={`${FOOTCL} ${compactCell} ${align(c.align || 'left')} ${c.className || ''}`.trim()}>
                    {val}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );

  if (!paginationEnabled) return table;

  return (
    <div>
      {table}
      <PaginationControls
        label={paginationLabel}
        page={activePage}
        pageCount={pageCount}
        pageSize={normalizedPageSize}
        totalRows={allRows.length}
        onPrevious={() => setPage((current) => clampPage(current - 1, pageCount))}
        onNext={() => setPage((current) => clampPage(current + 1, pageCount))}
      />
    </div>
  );
}
